import { useCallback, useEffect, useRef, useState } from "react";
import { Lightbulb, RefreshCw } from "lucide-react";
import { getSuggestedQuestions } from "@/app/lib/auditflowApi";

interface Props {
    projectId: string;
    documentId: string | null;
    onChipClick: (question: string) => void;
}

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

function getCacheKey(docId: string) {
    return `auditflow-suggestions-${docId}`;
}

function readCachedSuggestions(docId: string): string[] | null {
    if (typeof window === "undefined") return null;
    try {
        const raw = localStorage.getItem(getCacheKey(docId));
        if (!raw) return null;
        const parsed = JSON.parse(raw) as {
            suggestions: string[];
            cachedAt: number;
        };
        if (
            Array.isArray(parsed.suggestions) &&
            Date.now() - parsed.cachedAt < SEVEN_DAYS_MS
        ) {
            return parsed.suggestions;
        }
    } catch {
        /* ignore corrupt cache */
    }
    return null;
}

function extractFriendlyError(raw: string): string {
    try {
        const parsed = JSON.parse(raw) as { detail?: string };
        if (parsed.detail) return parsed.detail;
    } catch {
        /* not JSON — use raw */
    }
    return raw;
}

function writeCachedSuggestions(docId: string, suggestions: string[]) {
    if (typeof window === "undefined") return;
    try {
        localStorage.setItem(
            getCacheKey(docId),
            JSON.stringify({ suggestions, cachedAt: Date.now() }),
        );
    } catch {
        /* ignore quota errors */
    }
}

export function DocSuggestionChips({
    projectId,
    documentId,
    onChipClick,
}: Props) {
    const [suggestions, setSuggestions] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const abortRef = useRef<AbortController | null>(null);

    const fetchSuggestions = useCallback(
        async (docId: string, signal: AbortSignal) => {
            setLoading(true);
            setError(null);
            try {
                const { suggestions: data } = await getSuggestedQuestions(
                    projectId,
                    docId,
                );
                if (signal.aborted) return;
                const fresh = data ?? [];
                setSuggestions(fresh);
                writeCachedSuggestions(docId, fresh);
            } catch (err) {
                if (signal.aborted) return;
                console.warn(
                    "[DocSuggestionChips] failed to load suggestions:",
                    err,
                );
                // Don't overwrite cached suggestions on error
                setError(
                    err instanceof Error
                        ? extractFriendlyError(err.message)
                        : "Failed to load suggestions",
                );
            } finally {
                if (!signal.aborted) setLoading(false);
            }
        },
        [projectId],
    );

    useEffect(() => {
        if (!documentId) {
            setSuggestions([]);
            setError(null);
            setLoading(false);
            abortRef.current?.abort();
            return;
        }

        // Show cached data instantly while we fetch fresh data
        const cached = readCachedSuggestions(documentId);
        if (cached) {
            setSuggestions(cached);
        }

        // Always fetch — AbortController prevents stale updates if we switch docs
        const controller = new AbortController();
        abortRef.current = controller;
        void fetchSuggestions(documentId, controller.signal);

        return () => {
            controller.abort();
        };
    }, [documentId, fetchSuggestions]);

    if (!documentId) return null;

    const showSkeletons = loading && suggestions.length === 0;

    return (
        <div className="shrink-0 px-4 pt-3 pb-1">
            <div className="flex items-center gap-1.5 mb-2">
                <Lightbulb className="h-3.5 w-3.5 text-amber-500" />
                <span className="text-xs font-medium text-gray-500">
                    Suggested questions
                </span>
                {loading && (
                    <RefreshCw className="h-3 w-3 text-gray-400 animate-spin ml-1" />
                )}
            </div>
            {suggestions.length > 0 ? (
                <div className="flex flex-col gap-2">
                    {suggestions.map((q, i) => (
                        <button
                            key={i}
                            onClick={() => onChipClick(q)}
                            className="text-xs px-4 py-2 rounded-full border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-300 transition-colors text-left leading-snug whitespace-normal break-words w-full"
                            title={q}
                        >
                            {q}
                        </button>
                    ))}
                </div>
            ) : showSkeletons ? (
                <div className="flex flex-col gap-2">
                    {[1, 2, 3].map((i) => (
                        <div
                            key={i}
                            className="h-8 w-full rounded-full bg-gray-100 animate-pulse"
                        />
                    ))}
                </div>
            ) : error && suggestions.length === 0 ? (
                <p className="text-xs text-gray-400">{error}</p>
            ) : null}
        </div>
    );
}
